import React, { useContext, useState } from 'react';
import { ReloadOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Empty,
  Flex,
  Input,
  Space,
  Spin,
  Tabs,
  Tooltip,
  Typography,
} from 'antd';
import dayjs from 'dayjs';

import StorageReportChart from '@totem/components/administration/storageReporting/StorageReportChart';
import StorageReportContext from '@totem/components/administration/storageReporting/StorageReportContext';
import StorageReportKpis from '@totem/components/administration/storageReporting/StorageReportKpis';
import StorageReportTable from '@totem/components/administration/storageReporting/StorageReportTable';
import {
  ORGANIZATION_TYPE_DEACTIVATED,
  OrganizationStorage,
} from '@totem/components/administration/storageReporting/types';

const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm';

export const StorageReportExtra = () => {
  const { snapshot, onRefresh } = useContext(StorageReportContext);

  return (
    <Space>
      {snapshot && (
        <Typography.Text type="secondary">
          Snapshot {dayjs(snapshot.createdAt).format(DATE_TIME_FORMAT)}
        </Typography.Text>
      )}
      <Tooltip title="Refresh">
        <Button icon={<ReloadOutlined />} onClick={onRefresh} />
      </Tooltip>
    </Space>
  );
};

type TabProps = {
  organizations: OrganizationStorage[];
  isDeactivated: boolean;
};

const StorageReportTab = ({ organizations, isDeactivated }: TabProps) => {
  const { loading, snapshot } = useContext(StorageReportContext);
  const [search, setSearch] = useState('');
  const [hideEmpty, setHideEmpty] = useState(true);

  const searchText = search.trim().toLowerCase();
  const visible = organizations.filter(
    (organization) =>
      (!hideEmpty || organization.totalBytes > 0) &&
      (!searchText ||
        organization.name.toLowerCase().includes(searchText) ||
        organization.buildings.some((building) =>
          building.name.toLowerCase().includes(searchText),
        )),
  );

  return (
    <Flex vertical gap={16}>
      <StorageReportKpis
        organizations={organizations}
        isDeactivated={isDeactivated}
        recentDays={snapshot?.recentDays ?? 30}
      />
      <StorageReportChart organizations={organizations} />
      <Flex gap={16} align="center">
        <Input.Search
          allowClear
          placeholder="Search organizations or buildings"
          style={{ maxWidth: 320 }}
          onChange={(event) => setSearch(event.target.value)}
        />
        <Checkbox
          checked={hideEmpty}
          onChange={(event) => setHideEmpty(event.target.checked)}
        >
          Hide organizations with no data
        </Checkbox>
      </Flex>
      <StorageReportTable
        organizations={visible}
        isDeactivated={isDeactivated}
        loading={loading}
        exportName={
          isDeactivated ? 'storage-deactivated-orgs' : 'storage-active-orgs'
        }
      />
    </Flex>
  );
};

export const StorageReportLayout = () => {
  const { loading, snapshot } = useContext(StorageReportContext);

  if (!snapshot) {
    return loading ? (
      <Flex justify="center" style={{ padding: 48 }}>
        <Spin />
      </Flex>
    ) : (
      <Empty description="No storage snapshot yet. The first one is taken by the nightly job." />
    );
  }

  const active = snapshot.organizations.filter(
    (organization) => organization.type !== ORGANIZATION_TYPE_DEACTIVATED,
  );
  const deactivated = snapshot.organizations.filter(
    (organization) => organization.type === ORGANIZATION_TYPE_DEACTIVATED,
  );

  return (
    <Tabs
      items={[
        {
          key: 'active',
          label: `Active Organizations (${active.length})`,
          children: (
            <StorageReportTab organizations={active} isDeactivated={false} />
          ),
        },
        {
          key: 'deactivated',
          label: `Deactivated Organizations (${deactivated.length})`,
          children: (
            <StorageReportTab organizations={deactivated} isDeactivated />
          ),
        },
      ]}
    />
  );
};

export default StorageReportLayout;
